import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useI18n } from '@infrastructure/i18n';
import styled from 'styled-components';
import { Content } from '@xingternal/winery-ui/lib/Content';
import { Stack } from '@xingternal/winery-ui/lib/Stack';
import { Lightbox } from '@xingternal/winery-ui/lib/Lightbox';
import { Button } from '@xingternal/rebranding-adapter';
import { Text } from '@xingternal/winery-ui/lib/Text';

import { routes } from '@infrastructure/routing/routes';

const CenteredStack = styled(Stack)`
  display: flex;
  align-items: center;
`;

const Header = styled(Text).attrs({
  size: 6,
  color: 'petrol',
  as: 'h1',
})`
  text-align: center;
`;

const InfoText = styled(Text).attrs({
  color: 'secondaryText',
})`
  text-align: center;
`;

const displayTimeLeft = ({ i18n, timeLeft }) => {
  if (timeLeft > 0) {
    return i18n(i18ns.redirectingIn, { timeLeft });
  }

  return i18n(i18ns.redirectingNow);
};

export const i18ns = {
  sessionExpiredError: 'XSS_SESSION_EXPIRED_ERROR',
  loginButton: 'XSS_SESSION_EXPIRED_LOGIN_BUTTON',
  redirectingIn: 'XSS_SESSION_EXPIRED_REDIRECTING_IN',
  redirectingNow: 'XSS_SESSION_EXPIRED_REDIRECTING_NOW',
} as const;

export const DEFAULT_TIMEOUT = 10; //seconds

function useCountDownTimer(seconds: number): number {
  const [timeLeft, setTimeLeft] = useState<number>(seconds);

  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }

    const timerID = setInterval(() => tick({ timeLeft, setTimeLeft }), 1000);

    return function cleanup() {
      clearInterval(timerID);
    };
  });

  const tick = ({ timeLeft: value, setTimeLeft: callback }) => {
    callback(value - 1);
  };

  return timeLeft;
}

const LostSessionDialog = ({ seconds = DEFAULT_TIMEOUT }) => {
  const { i18n } = useI18n<typeof i18ns>();
  const history = useHistory();
  const [, setIsVisible] = useState(true);
  const timeLeft = useCountDownTimer(seconds);

  if (timeLeft === 0) {
    history.replace(routes.login);
  }

  return (
    <Lightbox
      showCloseIcon={false}
      onClose={() => setIsVisible(false)}
      clickOverlayToClose
    >
      <Lightbox.Body>
        <Content>
          <CenteredStack>
            <Header>{i18n(i18ns.sessionExpiredError)}</Header>
            <Button
              variant="primary"
              onClick={() => history.replace(routes.login)}
              data-testid="login-button"
            >
              {i18n(i18ns.loginButton)}
            </Button>
            <InfoText>{displayTimeLeft({ i18n, timeLeft })}</InfoText>
          </CenteredStack>
        </Content>
      </Lightbox.Body>
    </Lightbox>
  );
};

export { LostSessionDialog };
